import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, CardBody, Modal } from 'react-bootstrap';
import { CastType, IQuery, IWhere, IQuerySelectData } from '../solution/Interfaces';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateFull } from '../solution/utils/AppUtil';
import { Check, FileText, ScanLine, X, Printer, Pencil, Save } from 'lucide-react';
import ShowErrorAlert from '../ShowErrorAlert';


interface IStore {
    id: string;
    code: string;
    name: string;
    email: string
    customer_code: string;
    costing_code: string;
    sit_location: string;
    shopify_location_id: string;
    created: string;
    updated: string| null;
}

interface Props {
    store: IStore;
    onBack: () => void;
}

interface IIntransitIBT {
     id: string;
     created: string;
     created_by: string;
     updated: string | null;
     doc_num: string,
     category: string,
     internal: boolean;
     from_whs: string;
     notes: string | null;
     status: string;
     to_whs: string;
}

interface IIntransitIBTLine {
    id: string;
    created: string;
    updated: string | null;
    line_num: number;
    item_name: string;
    item_code: string;
    item_barcode: string;
    doc_num: string;
    qty_on_hand: number | null;
    qty_received: number | null;
    qty_scanned_out: number | null;
    qty_scanned_in: number | null;
    qty_sent: number | null;
}

interface ISelectedScannedItem {
  id: string;
  barcode: string;
  item_code: string;
  item_group: string;
  name: string;
  product_code: string;
}


export const IntransitIBT: React.FC<Props> = ({ store, onBack }) => {

  const { fetchData, responseErrorMessage, showErrorAlert, responseStatus }  = useAuth();
  const queryUrl: string = `${process.env.REACT_APP_BASE_URL}/${"query/data"}`;
  const queryUrlSelectNoCount: string = `${process.env.REACT_APP_BASE_URL}/${"query/select/data"}`;
  const fetchIntrasitIBTActionsQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/functions/INTERBRANCH-TRANSFER-ACTIONS/run`; 
  const fetchIntransitIBTLineActionsQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/functions/INTERBRANCH-TRANSFER-LINE-ACTIONS/run`; 


  const [intransitIBTState, setIntransitIBTState] = useState<IIntransitIBT[]>([]);
  const [intransitIBTLineState, setIntransitIBTLineState] = useState<IIntransitIBTLine[]>([]);
  const [selectedIntransitIBT, setSelectedIntransitIBT] = useState<IIntransitIBT | null>(null);
  const [selectedDocNum, setSelectedDocNum] = useState<string>("");
   
  const [submitRefreshData, setSubmitRefreshData] = useState<boolean>(false); 
  const [submitRefreshLineData, setSubmitRefreshLineData] = useState<boolean>(false);

  const [viewMode, setViewMode] = useState<'scan' | 'view' | 'default'>('default'); 
  const [loading, setLoading] = useState<boolean>(false);

  const [showUpdateStatusNormalIBTModal, setShowUpdateStatusNormalIBTModal] = useState<boolean>(false);

  //refs 
  const fileRef = useRef<HTMLInputElement | null>(null);
  const scanRef = useRef<HTMLInputElement | null>(null);

  const [selectedScannedItem, setSelectedScannedItem] = useState<ISelectedScannedItem | null>(null);  
  const [showCustomQuantitySection, setShowCustomQuantitySection] = useState<boolean>(false);
  const [customQuantity, setCustomQuantity] = useState<string>('1');

  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editedQty, setEditedQty] = useState<Record<string, number>>({}); 
  const [inputDraft, setInputDraft] = useState<Record<string, string>>({});
   
  const defaultWhere: IWhere[] = [
      {
          fullColumnName: "to_whs",
          value: store?.code,
          operator: "EQ",
          cast: CastType.STRING
      },
      {
          fullColumnName: "internal",
          value: false,
          operator: "EQ",
          cast: CastType.BOOLEAN
      },
      {
          fullColumnName: "status",
          value: ["SENT", "CLOSED", "RECEIVED"],
          operator: "IN",
          cast: CastType.STRING
      },
  ];

  const [queryState, setQueryState] = useState<IQuery>({
           tableName: "sa_ares_interbranch_transfer",
           select: [],
           groupBy: [], 
           join: [],
           orderBy: [],
           where: defaultWhere,
           mainTableOnly: false,
           includeExtraColumns: true,
           includeControlColumns: true,
           modifiedAfter: null,
           touchedBefore: null,
           isDirty: null,
           pageNum: 0,
           pageSize: 100,
           frontEndOnly: null
  });


  //Query Line State
  const [queryLineState, setQueryLineState] = useState<IQuery>({
      tableName: "sa_ares_interbranch_transfer_line",
      select: [],
      groupBy: [],
      join: [{
        relationship: 'item',
        joinType: "LEFT"
      }],
      orderBy: [ 
          {
              fullColumnName: "created",
              direction: "ASC",
              cast: CastType.TIMESTAMP
          }
      ],
      where:[],
      mainTableOnly: false,
      includeExtraColumns: true,
      includeControlColumns: true,
      modifiedAfter: null,
      touchedBefore: null,
      isDirty: null,
    //   pageNum: 0,
    //   pageSize: 100,
      frontEndOnly: null
  });
  

  const defaultQueryState: IQuerySelectData = {
          appCode: "ares",
          selectCode: "scan-item",  
          params: {}
      };   
        
  const [querySelectState, setQuerySelectState] = useState<IQuerySelectData>(defaultQueryState);

  useEffect(() => {
        setLoading(true); 
        fetchData(queryUrl, {method:"POST", body: JSON.stringify(queryState)}, processData, cleanUpLoader);
  }, [queryState, submitRefreshData]);

  useEffect(() => {
        if (viewMode === "scan" && scanRef.current) {
            scanRef.current.focus();
        }
    }, [viewMode]);
    
    useEffect(() => {
    const queryLine = {...queryLineState}
    queryLine.where = [
            {
            fullColumnName: "doc_num",
            value: selectedIntransitIBT?.doc_num, 
            operator: "EQ",
            cast: CastType.STRING
        }       
    ]
    setLoading(true); 
    fetchData(queryUrl, {method:"POST", body: JSON.stringify(queryLine)}, processLineData, cleanUpLoader);
    }, [selectedIntransitIBT, submitRefreshLineData]);


  const processData = (result: any) => {

      const intransitIBT: IIntransitIBT[] = [];

      for(const key in result?.data){
          intransitIBT.push({...result.data[key].data, ...result.data[key].control, ...result.data[key].undefined});
      }

      // Sort by created date descending
      intransitIBT.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());

      setIntransitIBTState(intransitIBT);
  }


   const processLineData = (result: any) => {

        const normalIBTLines: IIntransitIBTLine[] = [];

        for(const key in result?.data){

            const entry = result.data[key];
            const control = entry.control || {};
            const data = entry.data || {};

            normalIBTLines.push({
                id: control.id,
                created: control.created,
                updated: control.updated ?? null,
                line_num: data.line_num,
                item_name: data["item.name"],
                item_code: data.item_code,
                item_barcode: data["item.barcode"], 
                doc_num: data.doc_num,
                qty_on_hand: data.qty_on_hand,
                qty_received: data.qty_received,
                qty_scanned_out: data.qty_scanned_out,
                qty_scanned_in: data.qty_scanned_in,
                qty_sent: data.qty_sent,
                });
        }
        
        setIntransitIBTLineState(normalIBTLines);
  };

  //default clean up loader
  const cleanUpLoader = () => {
      setLoading(false);
  }

  //handle view button when clicked
  const handleViewClick = (intransitIBT : IIntransitIBT) => {
      setViewMode('view'); // Switch to view mode
      setSelectedIntransitIBT(intransitIBT);
  }

  //handle scan button when clicked
  const handleScanClick = (intransitIBT : IIntransitIBT) => {
      setViewMode('scan'); // Switch to Scan mode
      setSelectedIntransitIBT(intransitIBT);
  }

  // Add the event handler for "Enter" key press
  const handleScanKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, docNum: string) => {
        if (event.key === 'Enter') {
            event.preventDefault();  // Prevent default behavior (if needed)
            handleScanSubmit(docNum); 
        }
  };


  const handleScanSubmit = (docNum: string) => {

        setLoading(true);
        const scanValueItemCode = scanRef.current?.value.trim();
        if (!docNum || !scanValueItemCode) return;

        const queryPayload = {
          ...querySelectState,
          params: { ...querySelectState.params, scan_code: scanValueItemCode },
        };

        let shouldRunLineFetch = true;

        fetchData( queryUrlSelectNoCount,
          { method: "POST", body: JSON.stringify(queryPayload)},
          (result: any, status: string) => {
            if (status === "200") {
              const item = result?.data?.[0]?.data;
              const id = result?.data?.[0]?.id;

              if (item && id) {

                const itemGroup = item.item_group;

                const scannedItem: ISelectedScannedItem = {
                  id,
                  barcode: item.barcode,
                  item_code: item.item_code,
                  item_group: itemGroup,
                  name: item.name,
                  product_code: item.product_code,
                };

                setSelectedScannedItem(scannedItem);

                if (itemGroup === "Jibbitz") {
                  setShowCustomQuantitySection(true);
                  shouldRunLineFetch = false; // Don't run line fetch for Jibbitz
                }
              }
            }

            // Only run line fetch if allowed
            if (shouldRunLineFetch) {
             fetchData(fetchIntransitIBTLineActionsQueryUrl,
              { method:"POST", body: JSON.stringify({ doc_num: docNum, item_code: scanValueItemCode, qty_scanned_in: 1 })}, 
              (result: any, status: string) =>  { 
                if(status === "200"){
                   setSubmitRefreshLineData(prev => !prev); 
                }
              }, cleanUpLoader);
            } 
            else {
              cleanUpLoader(); // still clean up if skipping line fetch
            }
          }
        );

        if (scanRef.current) {
          scanRef.current.value = "";
        }
    };


    const handleQuantitySubmit = () => {

      if (!selectedScannedItem || !selectedIntransitIBT?.doc_num) return;

      const { item_code, barcode } = selectedScannedItem;

      const parsedQty = Math.max(1, parseInt(customQuantity) || 1);

      const payload = {
        doc_num: selectedIntransitIBT.doc_num,
        qty_scanned_in: parsedQty,
        item_code: item_code || barcode,
      };

      setLoading(true);

      fetchData(fetchIntransitIBTLineActionsQueryUrl,
          { method:"POST", body: JSON.stringify(payload)}, 
          (result: any, status: string) =>  { 
            if(status === "200"){
                 setShowCustomQuantitySection(false);
                 setSelectedScannedItem(null);
                 setCustomQuantity('1');
                 setSubmitRefreshLineData(prev => !prev); 
            }
          }, cleanUpLoader);
    };

   //handle to receieved ibt button when clicked
    const handleReceivedStatusIntransitIBTClick = (intransitIBT : IIntransitIBT) => {
        setViewMode('default'); // Switch to default mode
        setShowUpdateStatusNormalIBTModal(true);
        setSelectedDocNum(intransitIBT.doc_num);
    }

    //handle the submit for setting re normal ibt
    const handleRecievedIntransitIBTSubmit = () => {
        if(selectedDocNum !== ""){
            setLoading(true);
            fetchData(fetchIntrasitIBTActionsQueryUrl, 
                {method:"POST", body: JSON.stringify({ doc_num: selectedDocNum,  status: "RECEIVED" })}, 
                (result: any, status: string) =>  {
                    if(status === "200"){
                       // Causes refresh on data  
                       setSubmitRefreshData(prev => !prev);
                    }
                }, cleanUpLoader);
            setSelectedDocNum("");
        }else{
            return; 
        }
    }

    const handleEditClick = (id: string, initialQty: number) => {
        setEditingRowId(id);
        setEditedQty(prev => ({ ...prev, [id]: initialQty }));
        setInputDraft(prev => ({ ...prev, [id]: initialQty.toString() }));
    };

    const handleQtyDraftChange = (id: string, value: string) => {
        setInputDraft(prev => ({ ...prev, [id]: value }));
    };

    const handleBlur = (id: string) => {
        const draftValue = inputDraft[id];
        const parsed = parseInt(draftValue, 10);
        setEditedQty(prev => ({ ...prev, [id]: isNaN(parsed) ? 0 : parsed }));
    };


    const handleSaveClick = (id: string) => {
        const qty = editedQty[id];
        const item_code = intransitIBTLineState.find(line => line.id === id)?.item_code;
        const doc_num = selectedIntransitIBT?.doc_num;

        if (!item_code || qty === undefined || doc_num === undefined) return;

        const payload = {
        doc_num,
        item_code,
        qty_scanned_in: qty,
        mode: 'exact'
        };


        // Perform your save operation here (e.g., API call)
        fetchData(fetchIntransitIBTLineActionsQueryUrl,
        { method:"POST", body: JSON.stringify(payload)}, 
        (result: any, status: string) =>  { 
            if(status === "200"){
                setSubmitRefreshLineData(prev => !prev); 
                setEditingRowId(null); // exit edit mode
                setEditedQty({});
                setInputDraft({});
            }
        }, cleanUpLoader);
    };


  const DisabledScanBtn = () => {
        return (
            <span className="btn h-100 d-flex align-items-center gap-1" title="Scan"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
              >
              <ScanLine style={{ width: "20px", height: "20px", color: "#6c757d" }} />
            </span>
        ) 
    } 

    const DisabledCheckBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Submit"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
         <Check style={{ width: "20px", height: "20px", color: "#6c757d" }} />
        </span>
       )
    } 

    const DisabledEditQtyBtn = () => {
            return (
            <span className="btn h-100 d-flex align-items-center gap-1" title="Edit Quantity"
                style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
            >
            <Pencil style={{ width: "20px", height: "20px", color: "#6c757d" }} />
            </span>
        )
    } 

    const DisabledSaveQtyBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Save Quantity"
                style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
            >
            <Save style={{ width: "22px", height: "22px", color: "#6c757d" }} />
        </span>
        )
    } 



      //handles the back to intransit ibt.
    const handleBackToIntransitIBTS = () => {
        setViewMode('default');
        setSelectedIntransitIBT(null);
        setShowCustomQuantitySection(false);
        setSelectedScannedItem(null);
        setCustomQuantity('1');
        setEditingRowId(null); // exit edit mode
        setEditedQty({});
        setInputDraft({});
        fetchData(queryUrl, {method:"POST", body: JSON.stringify(queryState)}, processData, cleanUpLoader);
    }
   
     const handleCloseForm = () => {
      setSelectedScannedItem(null);
      setCustomQuantity('1');
      setShowCustomQuantitySection(false); 
    };


  const thStyle = {
      backgroundColor: '#f7f5f5',
      border: '1px solid #deebed',
      fontWeight: 600,
      padding: '10px 8px',
      fontSize: '0.85rem',
  };

  return (
    <>
    { showErrorAlert && <ShowErrorAlert message={responseErrorMessage} /> } 
    <Card>
     <Card.Header>
         <Card.Title>{`Receive Stock (IBT) [${store.name}]`}</Card.Title>                       
     </Card.Header>
     <CardBody>
        <div className="d-flex gap-1">
              <button
                className="btn btn-secondary h-100"
                onClick={() => {
                    if (viewMode === 'default') {
                        onBack();
                    } else {
                        handleBackToIntransitIBTS();
                    }
                    }}
                >
                { viewMode === 'default' ? "Back To Stock Transfers (IBT)" : "Back To Receive Stock IBT" }
            </button>
        </div>
        <br></br>     
          <div>
                  { /* Intransit IBT Table Start */ }
                  <table className="table table-bordered">
                            <thead>                           
                                <tr>
                                    <th style={thStyle}>Doc Number</th>
                                    <th style={thStyle}>From Warehouse</th>
                                    <th style={thStyle}>To Warehouse</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={thStyle}>Created</th>
                                    <th style={thStyle}>Notes</th>
                                    <th style={thStyle}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                               { loading && viewMode === "default" ? (
                                 <>
                                    <tr>
                                        <td colSpan={6} className="text-center">
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        </td>
                                    </tr>
                                 </>
                                 ) : (
                                    <>
                                     {!selectedIntransitIBT && viewMode === "default" && intransitIBTState.map((intransitIBT: IIntransitIBT) => (
                                      <tr key={intransitIBT.id}>
                                        <td>{intransitIBT.doc_num}</td>
                                        <td>{intransitIBT.from_whs}</td>
                                        <td>{intransitIBT.to_whs}</td>
                                        <td>{intransitIBT.status}</td>
                                        <td>{formatDateFull(intransitIBT.created)}</td>
                                        <td title={intransitIBT?.notes ?? ""}>
                                            <div style={{ 
                                                whiteSpace: "nowrap", 
                                                overflow: "hidden", 
                                                textOverflow: "ellipsis", 
                                                maxWidth: "150px" 
                                            }}>
                                                {intransitIBT.notes}
                                            </div>
                                        </td>
                                        <td className="d-flex gap-1">
                                            {intransitIBT.status === "SENT" ? (
                                                <button
                                                    className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                                    title="Scan"
                                                    onClick={() => handleScanClick(intransitIBT)}
                                                >
                                                    <ScanLine style={{ width: "20px", height: "20px" }} />
                                                </button>
                                                ) : (
                                                <DisabledScanBtn />
                                            )}

                                           {intransitIBT.status === "SENT" ? (
                                                <button
                                                    className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                                    title="Submit"
                                                    onClick={() => handleReceivedStatusIntransitIBTClick(intransitIBT)}
                                                >
                                                    <Check style={{ width: "20px", height: "20px" }} />
                                                </button>
                                            ) : (
                                                 <DisabledCheckBtn />
                                            )}

                                            <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='View'
                                                onClick={() => handleViewClick(intransitIBT)}
                                            >
                                                <FileText style={{ width: "20px", height: "20px" }}  /> 
                                            </button>

                                        </td>
                                     </tr>
                                   ))}
                                 </>
                                )}

                                 {selectedIntransitIBT && viewMode !== "default" &&
                                    <>
                                        <tr key={selectedIntransitIBT.id}>
                                            <td>{selectedIntransitIBT.doc_num}</td>
                                            <td>{selectedIntransitIBT.from_whs}</td>
                                            <td>{selectedIntransitIBT.to_whs}</td>
                                            <td>{selectedIntransitIBT.status}</td>
                                            <td>{formatDateFull(selectedIntransitIBT.created)}</td>
                                            <td title={selectedIntransitIBT?.notes ?? ""}>
                                            <div style={{ 
                                                whiteSpace: "nowrap", 
                                                overflow: "hidden", 
                                                textOverflow: "ellipsis", 
                                                maxWidth: "150px" 
                                            }}>
                                                {selectedIntransitIBT.notes}
                                            </div>
                                          </td>
                                        </tr>
                                        <tr>
                                            <td colSpan={9} style={{ textAlign: 'center' }}>
                                                   { viewMode === 'view' ? (null) : (
                                                    <>
                                                    {selectedScannedItem && showCustomQuantitySection ? (
                                                        <form
                                                        onSubmit={(e) => {
                                                            e.preventDefault();
                                                            handleQuantitySubmit();
                                                        }}
                                                        className="p-3 border rounded bg-light"
                                                        style={{ display: 'inline-block', textAlign: 'left', minWidth: '600px' }}
                                                        >
                                                        <div className="mb-3 position-relative">
                                                            <div className="d-flex justify-content-between align-items-center">
                                                            <h6 className="mb-0" style={{ fontWeight: 600 }}>Scanned Item Details</h6>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary btn-sm rounded-circle d-flex align-items-center justify-content-center"
                                                                style={{ width: '28px', height: '28px' }}
                                                                onClick={handleCloseForm}
                                                                aria-label="Close"
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                            </div>
                                                            <hr className="my-2" />
    
                                                            <div className="row">
                                                            <div className="col-md-6 mb-2">
                                                                <label className="form-label mb-0"><strong>Name:</strong></label>
                                                                <div className="form-control-plaintext">{selectedScannedItem.name}</div>
                                                            </div>
                                                            <div className="col-md-6 mb-2">
                                                                <label className="form-label mb-0"><strong>Item Group:</strong></label>
                                                                <div className="form-control-plaintext">{selectedScannedItem.item_group}</div>
                                                            </div>
                                                            <div className="col-md-6 mb-2">
                                                                <label className="form-label mb-0"><strong>Item Code:</strong></label>
                                                                <div className="form-control-plaintext">{selectedScannedItem.item_code}</div>
                                                            </div>
                                                            <div className="col-md-6 mb-2">
                                                                <label className="form-label mb-0"><strong>Barcode:</strong></label>
                                                                <div className="form-control-plaintext">{selectedScannedItem.barcode}</div>
                                                            </div>
                                                            </div>
                                                        </div>
    
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <div className="d-flex align-items-center">
                                                            <label htmlFor="custom_quantity" className="form-label mb-0 me-2"><strong>Quantity:</strong></label>
                                                                <input
                                                                type="number"
                                                                id="custom_quantity"
                                                                name="custom_quantity"
                                                                min={1}
                                                                value={customQuantity}
                                                                onChange={(e) => setCustomQuantity(e.target.value)}
                                                                className="form-control form-control-sm"
                                                                style={{ width: '100px' }}
                                                                />
                                                            </div>
    
                                                            <button type="submit" className="btn btn-dark btn-sm ms-3">
                                                            Save Qauntity
                                                            </button>
                                                        </div>
                                                        </form>
                                                    ) : (
                                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                        <span style={{ marginRight: '10px' }}>Scan</span>
                                                        <input
                                                        ref={scanRef}
                                                        id="scan_code"
                                                        name="scan_code"
                                                        type="text"
                                                        autoComplete="off"
                                                        className="form-control form-control-md w-25"
                                                        style={{ margin: 0 }}
                                                        onKeyDown={(e) => handleScanKeyDown(e, selectedIntransitIBT.doc_num)}
                                                        />
                                                    </div>
                                                    )}
                                                </>
                                                )}
                                            </td>
                                        </tr>
                                    </>
                                }  
                            </tbody>
                   </table>
                  { /* Intransit IBT Table  End */ }

                  {intransitIBTLineState && viewMode !== "default" &&
                    <table className="table table-bordered">
                        <thead>
                        <tr>
                            <th style={thStyle}>Line No</th>
                            <th style={thStyle}>Item Name</th>
                            <th style={thStyle}>Item Code</th>
                            <th style={thStyle}>Item Barcode</th>
                            {selectedIntransitIBT?.status !== "SENT" && <th style={{...thStyle, width: '112px'}}>QTY On Hand</th>}
                            <th style={{...thStyle, width: '134px'}}>QTY Scanned Out</th>
                            {selectedIntransitIBT?.status !== "SENT" && <th style={{...thStyle, width: '100px'}}>QTY Sent</th>}
                            <th style={{...thStyle, width: '130px'}}>QTY Scanned In</th>
                            <th style={{...thStyle, width: '120px'}}>QTY Recieved</th>
                            {selectedIntransitIBT?.status === "SENT" && (
                                <th style={thStyle}>Actions</th>
                            )}
                        </tr>
                        </thead>
                        <tbody>
                        {loading ? (
                            <tr>
                            <td
                                colSpan={selectedIntransitIBT?.status === "SENT" ? 9 : 9}
                                className="text-center"
                            >
                                <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                                </div>
                            </td>
                            </tr>
                        ) : (
                            intransitIBTLineState.map((intransitIBTLine: IIntransitIBTLine) => {
                            const { qty_scanned_out, qty_sent, qty_scanned_in, qty_received, qty_on_hand } = intransitIBTLine;
                            const isEditing = editingRowId === intransitIBTLine.id;

                            const hasValidationError =
                                selectedIntransitIBT?.status !== "SENT" &&
                                (
                                // Left-side checks: only compare if both are not null
                                (qty_sent != null && qty_scanned_out != null && qty_sent !== qty_scanned_out) ||
                                (qty_sent != null && qty_scanned_in != null && qty_scanned_in !== qty_sent) ||
                                (qty_scanned_in != null && qty_received != null && qty_received !== qty_scanned_in) ||

                                // Right-side checks: always compare, even if null
                                qty_sent !== qty_scanned_out ||
                                qty_scanned_out !== qty_scanned_in ||
                                qty_scanned_in !== qty_received
                                );

                            return (
                                <tr key={intransitIBTLine.id} style={{ backgroundColor: hasValidationError ? '#f7f7cd' : 'white' }}>
                                <td>{intransitIBTLine.line_num}</td>
                                <td>{intransitIBTLine.item_name}</td>
                                <td>{intransitIBTLine.item_code}</td>
                                <td>{intransitIBTLine.item_barcode}</td>
                                {selectedIntransitIBT?.status !== "SENT" && <td className="text-end">{qty_on_hand}</td>}
                                <td className="text-end">{qty_scanned_out}</td>
                                {selectedIntransitIBT?.status !== "SENT" && <td className="text-end">{qty_sent}</td>}
                                <td className="text-end">
                                    {isEditing ? (
                                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        style={{ maxWidth: "80px", fontSize: "0.85rem", padding: "0.25rem" }}
                                        value={inputDraft[intransitIBTLine.id] ?? ''}
                                        onChange={(e) => handleQtyDraftChange(intransitIBTLine.id, e.target.value)}
                                        onBlur={() => handleBlur(intransitIBTLine.id)}
                                        autoFocus
                                        onClick={(e) => (e.target as HTMLInputElement).select()}
                                        onFocus={(e) => (e.target as HTMLInputElement).select()}
                                        />
                                    </div>
                                    ) : (
                                            qty_scanned_in
                                    )}
                                </td>
                                <td className="text-end">{qty_received}</td>
                                 {selectedIntransitIBT?.status === "SENT" && (
                                           <td className='d-flex gap-1'>
                                            {isEditing ? (
                                                <>
                                                 { !inputDraft[intransitIBTLine.id]?.trim() ? (
                                                        <DisabledSaveQtyBtn/>
                                                    ) : (
                                                        <button
                                                        className="btn btn-secondary"
                                                        onClick={() => handleSaveClick(intransitIBTLine.id)}
                                                        title="Save Quantity"
                                                        >
                                                        <Save style={{ width: "20px", height: "20px" }} />
                                                        </button>
                                                    )
                                                }
                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={() => setEditingRowId(null)}
                                                    title="Cancel"
                                                >
                                                    <X style={{ width: "20px", height: "20px" }} />
                                                </button>
                                                </>
                                            ) : editingRowId === null ? (
                                                <button
                                                className="btn btn-secondary"
                                                onClick={() =>
                                                    handleEditClick(
                                                    intransitIBTLine.id,
                                                    intransitIBTLine.qty_scanned_in ?? 0
                                                    )
                                                }
                                                title="Edit Quantity"
                                                >
                                                <Pencil style={{ width: "20px", height: "20px" }} />
                                                </button>
                                            ) : (<DisabledEditQtyBtn/>) }
                                  </td>
                                )}
                             </tr>
                            );
                            })
                        )}
                        </tbody>
                    </table>
                    }

         </div>                                  
  
     </CardBody>

          {/* Modal To Send Internal IBT - Start */}
            <>
            <Modal show={showUpdateStatusNormalIBTModal} onHide={() => { setShowUpdateStatusNormalIBTModal(false); }}>
                <Modal.Header closeButton>
                    <Modal.Title>
                       {`Receive Normal IBT [${selectedDocNum}]`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                    <p>
                     { "Are you sure you would like to received intransit IBT "}
                    </p>
                </div>
                </Modal.Body>
                <Modal.Footer>
                <Button variant="primary" onClick={() => { setShowUpdateStatusNormalIBTModal(false); handleRecievedIntransitIBTSubmit();  }}>
                    Confirm
                </Button>
                <Button variant="secondary" onClick={() => { setShowUpdateStatusNormalIBTModal(false); } }>
                    Cancel
                </Button>
                </Modal.Footer>
            </Modal>
            </> 
         {/* Modal To Send Internal IBT - End */}


   </Card>
  </>
  )
}
